/**
 * useWebSocket — Plan 30 A5 minimal connection hook.
 *
 * The v1 platform-WS used to receive build_started / build_complete /
 * build_failed / preview_ready / marketing_started / marketing_complete /
 * task_graph / agent_start / agent_complete and ~10 other event types,
 * driving the build store from a single shared socket. That cross-channel
 * model is the source of the build- vs bld- ID confusion documented in
 * Plan 30 §"Why this plan exists".
 *
 * v2 lives entirely on the per-build socket opened by useBuildStream
 * (subscribed by MarketingHandoffBridge in BuildPage.tsx). That bridge
 * is the single source of build event truth.
 *
 * What this hook does now:
 *   1. Opens the legacy WS_URL (for non-build features still using the
 *      shared socket — content studio, deploy notifications, etc.)
 *   2. Sets `wsStore.connected` true/false
 *   3. Publishes the live socket via `wsStore.setSocket` for callers that
 *      want to subscribe to non-build channel-scoped events
 *   4. Exponential-backoff reconnect on unexpected close
 *   5. DOES NOT process any incoming messages — every received frame is
 *      dropped on the floor. Build state comes from useBuildStream.
 */
import { useEffect, useRef, useCallback } from 'react'
import { WS_URL } from '@/lib/constants'
import { isFinalWsClose, wsAuthProtocols } from '@/lib/wsAuth'
import { useWsStore } from '@/stores/wsStore'

const RECONNECT_BASE_MS = 1500
const RECONNECT_MAX_MS = 30_000
const CLEAN_CLOSE_CODES = new Set([1000, 1001])

export function useWebSocket() {
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const cleanedUpRef = useRef(false)
  const reconnectAttemptsRef = useRef(0)
  const setConnected = useWsStore((s) => s.setConnected)
  const setSocket = useWsStore((s) => s.setSocket)

  const connect = useCallback(() => {
    if (cleanedUpRef.current) return

    if (wsRef.current) {
      wsRef.current.onclose = null
      wsRef.current.close()
    }

    // E5b (R-301/R-302): authenticate at the handshake; signed out, open nothing.
    const protocols = wsAuthProtocols()
    if (!protocols) {
      setConnected(false)
      return
    }
    const ws = new WebSocket(WS_URL, protocols)
    wsRef.current = ws
    setSocket(ws)

    ws.onopen = () => {
      if (cleanedUpRef.current) { ws.close(); return }
      setConnected(true)
      reconnectAttemptsRef.current = 0
    }

    // No event processing — every incoming frame is intentionally dropped.
    // Build state flows through useBuildStream instead. Non-build
    // features that need to subscribe should read `wsStore.socket` and
    // attach their own message listener.
    ws.onmessage = () => { /* intentional no-op */ }

    ws.onclose = (event: CloseEvent) => {
      if (cleanedUpRef.current) return
      wsRef.current = null
      setConnected(false)
      setSocket(null)

      if (CLEAN_CLOSE_CODES.has(event.code) || isFinalWsClose(event.code)) return

      const delay = Math.min(
        RECONNECT_BASE_MS * 2 ** reconnectAttemptsRef.current,
        RECONNECT_MAX_MS,
      )
      reconnectAttemptsRef.current += 1
      reconnectTimeoutRef.current = setTimeout(() => {
        if (!cleanedUpRef.current) connect()
      }, delay)
    }

    ws.onerror = () => {
      // Mirror the legacy hook: don't surface errors to the UI — the
      // close handler's reconnect loop will retry. Silent failure is
      // correct here; the connection indicator (wsStore.connected) is
      // the user's signal.
    }
  }, [setConnected, setSocket])

  useEffect(() => {
    cleanedUpRef.current = false
    connect()

    return () => {
      cleanedUpRef.current = true
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current)
      if (wsRef.current) {
        wsRef.current.onopen = null
        wsRef.current.onmessage = null
        wsRef.current.onclose = null
        wsRef.current.onerror = null
        wsRef.current.close(1000, 'client unmount')
        wsRef.current = null
      }
      setConnected(false)
      setSocket(null)
    }
  }, [connect, setConnected, setSocket])
}
